/*
 * Deskon - on a product page, the header nav bar shows the collection the visitor came from, and
 * nothing otherwise.
 *
 * THE RULE, IN ONE LINE: the bar answers "which part of the shop am I browsing", and that question
 * only has an answer when the visitor actually walked in through a collection that the menu lists.
 *
 *   Portable Lamp -> a lamp        the bar stays under Portable Lamp, even when that same lamp also
 *                                  sits in Table Lamps. Where you came from wins over where else
 *                                  the product happens to be filed.
 *   Bestsellers -> a lamp          no bar at all. Bestsellers is a real collection but it is not a
 *                                  menu item, so there is nothing truthful to light.
 *   the homepage row -> a lamp     no bar either. Clicking a product out of a homepage section is
 *                                  not browsing a collection, and the memory is cleared on the way
 *                                  through the homepage precisely so an older visit cannot answer
 *                                  for it.
 *   a Google or Shopping click     no bar. There is no path to report.
 *
 * WHICH IS WHY THE SERVER RENDERS NO BAR ON A PRODUCT PAGE. blocks/_header-menu.liquid used to
 * mark the first menu item whose collection contained the product; it was the only thing Liquid
 * could compute, and it was wrong at least as often as it was right - Elsin is in Bestsellers and
 * in Table Lamps, so a visitor coming from Portable Lamp was told "Table Lamps". That was removed
 * when this script was given the whole job. The cost is stated plainly: with JavaScript off, a
 * product page has no bar. It is a piece of orientation, not navigation - every link in the row
 * still works - and a wrong bar is worse than no bar.
 *
 * WHY A SESSION MEMORY RATHER THAN THE URL. Shopify's own answer is `within: collection`, which
 * turns a card's link into /collections/portable-lamp/products/x and hands the collection back to
 * Liquid. It would mean editing every card link in the theme (blocks/product-title.liquid and
 * snippets/card-gallery.liquid, two links each) and changing the URL of every product link on the
 * site, and it still would not cover the homepage row. Reading the last collection actually
 * visited covers every case above with none of that.
 *
 * sessionStorage, not localStorage, on purpose: this is the current visit's path and it should be
 * gone the next time the shop is opened fresh. Every access is wrapped, because a browser in
 * private mode can throw on the very first read; a failure simply means no bar.
 */
(function () {
  const STORAGE_KEY = 'deskon:last-collection';
  const ACTIVE_CLASS = 'menu-list__link--active';
  const LINK_SELECTOR = '.header-menu .menu-list__link';

  let applyQueued = false;

  /* Matches /collections/portable-lamp and /collections/portable-lamp/products/x alike, and
     tolerates a locale prefix such as /fr/collections/... because it is not anchored to the start. */
  function collectionHandleFromPath(pathname) {
    const match = pathname.match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function isProductPage(pathname) {
    return pathname.indexOf('/products/') !== -1;
  }

  function read() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function write(handle) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, handle);
    } catch (error) {
      /* Private mode, blocked storage: the feature degrades to no bar, which is the safe half. */
    }
  }

  function forget() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* Same. */
    }
  }

  /*
   * THE CLEARING BRANCH IS THE IMPORTANT ONE, and it is what makes the homepage case work. Without
   * it the memory would survive any detour: browse Table Lamps, go back to the homepage, click a
   * product in the Bestsellers row, and the row would still claim Table Lamps because that was the
   * last collection seen at some point in the visit. A product page is left alone - it is the page
   * being explained, not a move to a new context.
   */
  function syncMemory() {
    const pathname = window.location.pathname;
    const handle = collectionHandleFromPath(pathname);

    if (isProductPage(pathname)) {
      /*
       * A product URL carrying a collection is an explicit context and is trusted over whatever
       * was stored. This theme's own links never produce that form today, but a link shared from
       * elsewhere can, and it costs nothing to honour.
       */
      if (handle) write(handle);
      return;
    }

    if (handle) {
      write(handle);
      return;
    }

    forget();
  }

  function navLinkFor(handle, links) {
    if (!handle) return null;

    const wanted = `/collections/${handle}`;
    let target = null;

    links.forEach((link) => {
      if (target || !link.href) return;

      let pathname;

      try {
        pathname = new URL(link.href, window.location.origin).pathname;
      } catch (error) {
        return;
      }

      /* An exact tail match, so /collections/lamps can never answer for /collections/desk-lamps. */
      if (pathname === wanted || pathname.endsWith(wanted)) target = link;
    });

    return target;
  }

  function apply() {
    applyQueued = false;

    const links = document.querySelectorAll(LINK_SELECTOR);

    if (!links.length || !isProductPage(window.location.pathname)) return;

    const target = navLinkFor(read(), links);

    /*
     * Clearing first and lighting second, in that order, is what guarantees only one bar: on a
     * product page nothing else in the theme sets this class, so the row ends up with exactly the
     * target, or with nothing when there is no honest answer.
     */
    links.forEach((link) => link.classList.remove(ACTIVE_CLASS));

    if (target) target.classList.add(ACTIVE_CLASS);
  }

  function queueApply() {
    if (applyQueued) return;

    applyQueued = true;
    requestAnimationFrame(apply);
  }

  syncMemory();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueApply);
  } else {
    queueApply();
  }

  /*
   * Selecting a variant re-renders the product section through the theme's morphing, and the header
   * can come back with it. The filter keeps this callback from doing any work on the many DOM
   * changes that have nothing to do with the header - the cart drawer opening, a quantity changing
   * - since it runs for all of them.
   */
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element && (node.matches('.header-menu') || node.querySelector(LINK_SELECTOR))) {
          queueApply();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* Back-button restores from the bfcache with no DOM mutation to notice, and on a different page
     than the one this script last ran against. */
  window.addEventListener('pageshow', () => {
    syncMemory();
    queueApply();
  });
})();
